import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { posRechargeService } from "../services/posRecharge.service";

export const fetchPosRechargeList = createAsyncThunk(
    "posRecharge/fetchAll",
    async (_, thunkAPI) => {
        try {
            return await posRechargeService.getPosRechargeList();
        } catch (err) {
            return thunkAPI.rejectWithValue(
                err?.response?.data?.message || "Failed to fetch POS recharge list"
            );
        }
    }
);

export const fetchPosRechargesByDateRange = createAsyncThunk(
    "posRecharge/fetchByDateRange",
    async (filter, thunkAPI) => {
        try {
            return await posRechargeService.getPosRechargesByDateRange(filter);
        } catch (err) {
            return thunkAPI.rejectWithValue(
                err?.response?.data?.message || "Failed to fetch POS recharge list by date range"
            );
        }
    }
);

export const fetchPosRechargeById = createAsyncThunk(
    "posRecharge/fetchById",
    async (id, thunkAPI) => {
        try {
            return await posRechargeService.getPosRechargeById(id);
        } catch (err) {
            return thunkAPI.rejectWithValue(
                err?.response?.data?.message || "Failed to fetch POS recharge"
            );
        }
    }
);

export const fetchPosRechargesByPos = createAsyncThunk(
    "posRecharge/fetchByPos",
    async (posId, thunkAPI) => {
        try {
            return await posRechargeService.getPosRechargesByPos(posId);
        } catch (err) {
            return thunkAPI.rejectWithValue(
                err?.response?.data?.message || "Failed to fetch POS recharges by POS"
            );
        }
    }
);

export const addPosRecharge = createAsyncThunk(
    "posRecharge/add",
    async (recharge, thunkAPI) => {
        try {
            return await posRechargeService.createPosRecharge(recharge);
        } catch (err) {
            return thunkAPI.rejectWithValue(
                err?.response?.data?.message || "Failed to create POS recharge"
            );
        }
    }
);

export const editPosRecharge = createAsyncThunk(
    "posRecharge/edit",
    async ({ id, recharge }, thunkAPI) => {
        try {
            return await posRechargeService.updatePosRecharge(id, recharge);
        } catch (err) {
            return thunkAPI.rejectWithValue(
                err?.response?.data?.message || "Failed to update POS recharge"
            );
        }
    }
);

export const removePosRecharge = createAsyncThunk(
    "posRecharge/remove",
    async (id, thunkAPI) => {
        try {
            return await posRechargeService.deletePosRecharge(id);
        } catch (err) {
            return thunkAPI.rejectWithValue(
                err?.response?.data?.message || "Failed to delete POS recharge"
            );
        }
    }
);

export const removePosRechargeHard = createAsyncThunk(
    "posRecharge/removeHard",
    async (id, thunkAPI) => {
        try {
            return await posRechargeService.hardDeletePosRecharge(id);
        } catch (err) {
            return thunkAPI.rejectWithValue(
                err?.response?.data?.message || "Failed to hard delete POS recharge"
            );
        }
    }
);

const initialState = {
    posRechargeList: [],
    selectedPosRecharge: null,
    totalCount: 0,
    loading: {
        fetch: false,
        create: false,
        edit: false,
        delete: false,
        details: false,
    },
    error: null,
    showModal: false,
    showDeleteModal: false,
};

// const replacePosRechargeInList = (list, updatedRecharge) =>
//     list.map((recharge) =>
//         recharge.rechargeId === updatedRecharge.rechargeId ? updatedRecharge : recharge
//     );

const posRechargeSlice = createSlice({
    name: "posRecharge",
    initialState,
    reducers: {
        openModal: (state) => {
            state.showModal = true;
            state.error = null;
        },
        closeModal: (state) => {
            state.showModal = false;
            state.selectedPosRecharge = null;
        },
        openDeleteModal: (state) => {
            state.showDeleteModal = true;
            state.error = null;
        },
        closeDeleteModal: (state) => {
            state.showDeleteModal = false;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPosRechargeList.pending, (state) => {
                state.loading.fetch = true;
                state.error = null;
            })
            .addCase(fetchPosRechargeList.fulfilled, (state, action) => {
                state.loading.fetch = false;
                state.posRechargeList = action.payload.items ?? [];
                state.totalCount = action.payload.totalCount ?? 0;
            })
            .addCase(fetchPosRechargeList.rejected, (state, action) => {
                state.loading.fetch = false;
                state.error = action.payload;
            })

            .addCase(fetchPosRechargesByDateRange.pending, (state) => {
                state.loading.fetch = true;
                state.error = null;
            })
            .addCase(fetchPosRechargesByDateRange.fulfilled, (state, action) => {
                state.loading.fetch = false;
                state.posRechargeList = action.payload.items ?? [];
                state.totalCount = action.payload.totalCount ?? 0;
            })
            .addCase(fetchPosRechargesByDateRange.rejected, (state, action) => {
                state.loading.fetch = false;
                state.error = action.payload;
            })

            .addCase(fetchPosRechargeById.pending, (state) => {
                state.loading.details = true;
                state.error = null;
            })
            .addCase(fetchPosRechargeById.fulfilled, (state, action) => {
                state.loading.details = false;
                state.selectedPosRecharge = action.payload;
            })
            .addCase(fetchPosRechargeById.rejected, (state, action) => {
                state.loading.details = false;
                state.error = action.payload;
            })

            .addCase(fetchPosRechargesByPos.pending, (state) => {
                state.loading.fetch = true;
                state.error = null;
            })
            .addCase(fetchPosRechargesByPos.fulfilled, (state, action) => {
                state.loading.fetch = false;
                state.posRechargeList = action.payload.items ?? [];
                state.totalCount = action.payload.totalCount ?? 0;
            })
            .addCase(fetchPosRechargesByPos.rejected, (state, action) => {
                state.loading.fetch = false;
                state.error = action.payload;
            })

            .addCase(addPosRecharge.pending, (state) => {
                state.loading.create = true;
                state.error = null;
            })
            .addCase(addPosRecharge.fulfilled, (state, action) => {
                state.loading.create = false;
                if (action.payload) {
                    state.posRechargeList.unshift(action.payload);
                    state.totalCount += 1;
                }
                state.showModal = false;
            })
            .addCase(addPosRecharge.rejected, (state, action) => {
                state.loading.create = false;
                state.error = action.payload;
            })

            .addCase(editPosRecharge.pending, (state) => {
                state.loading.edit = true;
                state.error = null;
            })
            .addCase(editPosRecharge.fulfilled, (state) => {
                state.loading.edit = false;
                state.showModal = false;
            })
            .addCase(editPosRecharge.rejected, (state, action) => {
                state.loading.edit = false;
                state.error = action.payload;
            })

            .addCase(removePosRecharge.pending, (state) => {
                state.loading.delete = true;
                state.error = null;
            })
            .addCase(removePosRecharge.fulfilled, (state, action) => {
                state.loading.delete = false;

                const deletedId =
                    action.meta?.arg ??
                    action.payload?.rechargeId ??
                    action.payload?.id ??
                    action.payload;

                state.posRechargeList = state.posRechargeList.filter(
                    (recharge) => recharge.rechargeId !== deletedId
                );

                state.totalCount = Math.max(0, state.totalCount - 1);
                state.showDeleteModal = false;
            })
            .addCase(removePosRecharge.rejected, (state, action) => {
                state.loading.delete = false;
                state.error = action.payload;
            })

            .addCase(removePosRechargeHard.pending, (state) => {
                state.loading.delete = true;
                state.error = null;
            })
            .addCase(removePosRechargeHard.fulfilled, (state, action) => {
                state.loading.delete = false;
                state.posRechargeList = state.posRechargeList.filter(
                    (recharge) => recharge.rechargeId !== action.payload
                );
                state.totalCount = Math.max(0, state.totalCount - 1);
                state.showDeleteModal = false;
            })
            .addCase(removePosRechargeHard.rejected, (state, action) => {
                state.loading.delete = false;
                state.error = action.payload;
            });
    },
});

export const {
    openModal,
    closeModal,
    openDeleteModal,
    closeDeleteModal,
    clearError,
} = posRechargeSlice.actions;

export default posRechargeSlice.reducer;